import { forwardRef } from "react";
import { margin, width, height, minWidth, minHeight, maxWidth, maxHeight, overflow, overflowX, overflowY } from "styled-system";
import styled from "styled-components";
import cx from "classnames";
import Tabs from "../tabs";
import Box from "../box";
import Spinner from "../spinner";
import { clearfix } from "../../styles/mixins";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const StyledSpinner = styled(Spinner).withConfig({
  displayName: "card__StyledSpinner",
  componentId: "sc-19a32xd-0"
})(["height:100%;.ant-spin{display:flex;align-items:center;justify-content:center;height:100%;}"]);
const StyledCard = styled.div.withConfig({
  displayName: "card__StyledCard",
  componentId: "sc-19a32xd-1"
})(["display:flex;flex-direction:column;position:relative;background-color:", ";border-radius:", "px;transition:all 0.3s;overflow:hidden;", " ", " ", " ", " ", " ", " ", " ", " ", " ", " &[hidden]{display:none !important;}&.ant-card-bordered{border:", ";}.ant-card-head{min-height:45px;padding:0 ", "px;color:", ";font-size:", "px;border-bottom:", ";border-radius:", "px ", "px 0 0;", " &-wrapper{display:flex;align-items:center;}&-title{display:inline-block;flex:1;padding:", "px 0;font-family:", ";overflow:hidden;white-space:nowrap;text-overflow:ellipsis;}.ant-tabs{clear:both;margin-bottom:-9px;color:", ";font-size:", "px;font-weight:normal;&-bar{border-bottom:", ";}}}.ant-card-extra{float:right;margin-left:auto;padding:", "px 0;color:", ";font-size:", "px;font-weight:normal;}&.ant-card-contain-tabs[data-tab-type=\"line\"][data-tab-placement=\"bottom\"] > .ant-card-head{.ant-card-head-title,.ant-card-extra{padding-bottom:0;}}.ant-card-body{flex:auto;padding:", "px;", "}.ant-card-actions{margin:0;padding:0;list-style:none;border-top:", ";", " & > li{float:left;margin:12px 0;text-align:center;color:", ";> span{position:relative;display:block;min-width:32px;font-size:12px;line-height:22px;text-transform:uppercase;font-family:", ";cursor:pointer;&:hover{color:", ";transition:color ", ";}a:not(.ant-btn),> .anticon{display:inline-block;width:100%;line-height:22px;transition:color 0.3s;}a:not(.ant-btn){color:", ";&:hover{color:", ";}}> .anticon{font-size:16px;line-height:22px;&:hover{color:", ";}}}&:not(:last-child){border-right:", ";}}}&.ant-card-inner{&.ant-card-bordered{border:", ";}.ant-card-head{background:", ";.ant-card-head-title{font-size:", "px;}}}&.ant-card-small{> .ant-card-head{min-height:38px;padding:0 ", "px;font-size:", "px;.ant-card-head-title{padding:", "px 0;}.ant-card-extra{padding:", "px 0;font-size:", "px;}}&.ant-card-contain-tabs[data-tab-type=\"line\"]{&[data-tab-placement=\"bottom\"] > .ant-card-head{.ant-card-head-title,.ant-card-extra{padding-bottom:0;}}&[data-tab-placement=\"right\"] > .ant-card-head{.ant-tabs-tab{padding-top:10px;padding-bottom:10px;}}}> .ant-card-body{padding:", "px;}}&.ant-card-compact > .ant-card-body{padding:0;}&.ant-card-loading{overflow:hidden;.ant-card-body{user-select:none;}}&[data-tab-placement=\"right\"]{&.ant-card-contain-tabs > .ant-card-head{display:flex;.ant-card-head-wrapper{flex-grow:1;}.ant-tabs{margin-left:16px;align-self:flex-end;}}&.ant-card-contain-tabs[data-tab-type=\"line\"] > .ant-card-head{.ant-tabs-tab{padding-top:18px;padding-bottom:18px;}}}"], p => p.theme.colors.bg.component, p => p.theme.radii.base, margin, width, height, minWidth, minHeight, maxWidth, maxHeight, overflow, overflowX, overflowY, p => p.theme.borders.base, p => p.theme.space.md, p => p.theme.colors.heading, p => p.theme.fontSizes.lg, p => p.theme.borders.split, p => p.theme.radii.base, p => p.theme.radii.base, clearfix, p => p.theme.space.md, p => p.theme.fonts.secondary, p => p.theme.colors.text, p => p.theme.fontSizes.md, p => p.theme.borders.split, p => p.theme.space.sm, p => p.theme.colors.text, p => p.theme.fontSizes.md, p => p.theme.space.md, clearfix, p => p.theme.borders.split, clearfix, p => p.theme.colors.gray.base, p => p.theme.fonts.secondary, p => p.theme.colors.primary.base, p => p.theme.animationDurations.slow, p => p.theme.colors.gray.base, p => p.theme.colors.primary.base, p => p.theme.colors.primary.base, p => p.theme.borders.split, p => p.theme.borders.split, p => p.theme.colors.light.bg, p => p.theme.fontSizes.md, p => p.theme.space.sm, p => p.theme.fontSizes.md, p => p.theme.space.sm, p => p.theme.space.sm, p => p.theme.fontSizes.md, p => p.theme.space.sm);
function renderActions(actions) {
  return actions.map((action, index) => /*#__PURE__*/_jsx(Box, {
    as: "li",
    width: `${100 / actions.length}%`,
    children: /*#__PURE__*/_jsx("span", {
      children: action
    })
  }, `action-${index}`));
}
export const Card = /*#__PURE__*/forwardRef((_ref, ref) => {
  let {
    size = "default",
    compact = false,
    bordered = true,
    loading = false,
    inner = false,
    title,
    extra,
    actions,
    activeTabKey,
    defaultActiveTabKey,
    tabList,
    tabPlacement = "bottom",
    tabBarExtraContent,
    tabProps = {},
    onTabChange,
    headStyle,
    bodyStyle,
    className,
    children,
    ...rest
  } = _ref;
  const hasActiveTabKey = activeTabKey !== undefined;
  const tabType = tabProps?.type === "card" ? "card" : "line";
  const extraProps = {
    ...tabProps,
    [hasActiveTabKey ? "activeKey" : "defaultActiveKey"]: hasActiveTabKey ? activeTabKey : defaultActiveTabKey,
    size: size === "small" ? size : "middle",
    tabBarExtraContent
  };
  const handleTabChange = key => {
    onTabChange?.(key);
  };
  const tabs = tabList && tabList.length > 0 ? /*#__PURE__*/_jsx(Tabs, {
    ...extraProps,
    onChange: handleTabChange,
    children: tabList.map(item => /*#__PURE__*/_jsx(Tabs.TabPane, {
      tab: item.tab,
      disabled: item.disabled
    }, item.key))
  }) : null;
  const head = title || extra || tabs ? /*#__PURE__*/_jsxs("div", {
    className: "ant-card-head",
    style: headStyle,
    children: [/*#__PURE__*/_jsxs("div", {
      className: "ant-card-head-wrapper",
      children: [title && /*#__PURE__*/_jsx("div", {
        className: "ant-card-head-title",
        children: title
      }), extra && /*#__PURE__*/_jsx("div", {
        className: "ant-card-extra",
        children: extra
      })]
    }), tabs]
  }) : null;
  const body = /*#__PURE__*/_jsx("div", {
    className: "ant-card-body",
    style: bodyStyle,
    children: loading ? /*#__PURE__*/_jsx(StyledSpinner, {}) : children
  });
  const actionDom = actions && actions.length > 0 ? /*#__PURE__*/_jsx("ul", {
    className: "ant-card-actions",
    children: renderActions(actions)
  }) : null;
  const classString = cx("ant-card", className, {
    [`ant-card-${size}`]: size,
    "ant-card-compact": compact,
    "ant-card-bordered": bordered,
    "ant-card-loading": loading,
    "ant-card-inner": inner,
    "ant-card-contain-tabs": !!tabList && tabList.length > 0
  });
  return /*#__PURE__*/_jsxs(StyledCard, {
    ref: ref,
    ...rest,
    className: classString,
    "data-tab-placement": tabPlacement,
    "data-tab-type": tabType,
    children: [head, body, actionDom]
  });
});
export default Card;